/**
 * ShopDetailPage — full editing page for a single shop.
 * Route: /shops/:id
 * Includes: sections management, scan triggers, location editing,
 *           visibility toggle, share link, delete.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useShopStore, ScanRecord } from '../../stores/shopStore';
import ScanHistorySection from './ScanHistorySection';
import FrameGalleryOverlay from './FrameGalleryOverlay';
import { useAuthStore } from '../../stores/authStore';
import { apiUrl } from '../../config';
import { useTranslation } from 'react-i18next';
import './ShopDetailPage.css';

export default function ShopDetailPage() {
  const { t } = useTranslation('shops');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { shops, loading, scans, fetchShops, deleteShop, addSection, deleteSection, renameSection, updateSection, updateShop, fetchShopScans, deleteScan, retryScan, initiateTransfer, cancelTransfer } = useShopStore();
  const token = useAuthStore((s) => s.token);
  const authUser = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();

  const [newSectionName, setNewSectionName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmTransfer, setConfirmTransfer] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [sectionPosterErrors, setSectionPosterErrors] = useState<Set<string>>(new Set());

  // Edit name state
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');

  // Metadata editing state
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDescription, setMetaDescription] = useState('');
  const [metaAddress, setMetaAddress] = useState('');
  const [metaPhone, setMetaPhone] = useState('');
  const [metaWhatsapp, setMetaWhatsapp] = useState('');
  const [metaOpeningHours, setMetaOpeningHours] = useState('');

  // Section delete confirmation
  const [confirmDeleteSection, setConfirmDeleteSection] = useState<string | null>(null);

  // Section rename state
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editSectionName, setEditSectionName] = useState('');

  // Poster frame picker state
  const [pickingPosterSection, setPickingPosterSection] = useState<string | null>(null);
  const [sectionFrames, setSectionFrames] = useState<{ index: number; url: string }[]>([]);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [posterCacheBust, setPosterCacheBust] = useState<Record<string, number>>({});

  // Frame gallery state
  const [gallerySection, setGallerySection] = useState<{ sectionId: string; name: string } | null>(null);

  const handleSectionPosterError = useCallback((sectionId: string) => {
    setSectionPosterErrors((prev) => new Set(prev).add(sectionId));
  }, []);

  const shop = shops.find((s) => s.id === id) || null;
  const shopScans = id ? scans[id] || [] : [];

  // Fetch shops if not loaded yet (e.g. direct URL access)
  useEffect(() => {
    if (shops.length === 0) fetchShops();
  }, []);

  // Fetch scans for this shop
  useEffect(() => {
    if (id) fetchShopScans(id);
  }, [id]);

  // Handle location picked from LocationPickerPage — auto-save directly
  useEffect(() => {
    const pickedLat = searchParams.get('pickedLat');
    const pickedLng = searchParams.get('pickedLng');

    if (pickedLat && pickedLng && id) {
      const lat = parseFloat(pickedLat);
      const lng = parseFloat(pickedLng);
      if (!isNaN(lat) && !isNaN(lng)) {
        updateShop(id, { location: { lat, lng } }).catch(() => {});
      }
      searchParams.delete('pickedLat');
      searchParams.delete('pickedLng');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  if (!shop) {
    return (
      <div className="shop-detail-page">
        <header className="shop-detail-header">
          <button className="shop-detail-back" onClick={() => navigate('/shops')}>{t('common:back')}</button>
          <h1>{loading ? t('common:loadingDots') : t('shopNotFound')}</h1>
        </header>
      </div>
    );
  }

  const isPublic = shop.visibility === 'public';

  // Group scans by sectionId
  const scansBySection: Record<string, ScanRecord[]> = {};
  for (const scan of shopScans) {
    if (!scansBySection[scan.sectionId]) scansBySection[scan.sectionId] = [];
    scansBySection[scan.sectionId].push(scan);
  }

  function getLocLat(loc: { lat: number; lng: number }) { return loc.lat ?? 0; }
  function getLocLng(loc: { lat: number; lng: number }) { return loc.lng ?? 0; }

  function handlePickLocationOnMap() {
    const params = new URLSearchParams({ returnTo: `/shops/${id}` });
    const loc = shop?.location;
    if (loc) {
      params.set('lat', getLocLat(loc).toString());
      params.set('lng', getLocLng(loc).toString());
    }
    navigate(`/location-picker?${params.toString()}`);
  }

  async function handleAddSection() {
    if (!id || !newSectionName.trim()) return;
    await addSection(id, newSectionName.trim());
    setNewSectionName('');
  }

  function handleScan(sectionId: string, sectionName: string) {
    const params = new URLSearchParams({ shopId: id!, sectionId, sectionName });
    navigate(`/scan?${params.toString()}`);
  }

  async function handleToggleVisibility() {
    if (!id) return;
    const newVis = isPublic ? 'private' : 'public';
    await updateShop(id, { visibility: newVis });
  }

  function handleStartEditName() {
    setEditingName(true);
    setEditName(shop?.name || '');
  }

  async function handleSaveName() {
    if (!id || !editName.trim()) return;
    await updateShop(id, { name: editName.trim() });
    setEditingName(false);
  }

  function handleStartEditMeta() {
    setEditingMeta(true);
    setMetaDescription(shop?.description || '');
    setMetaAddress(shop?.address || '');
    setMetaPhone(shop?.phone || authUser?.phone || '');
    setMetaWhatsapp(shop?.whatsapp || authUser?.whatsapp || '');
    setMetaOpeningHours(shop?.openingHours || '');
  }

  async function handleSaveMeta() {
    if (!id) return;
    await updateShop(id, {
      description: metaDescription,
      address: metaAddress,
      phone: metaPhone,
      whatsapp: metaWhatsapp,
      openingHours: metaOpeningHours,
    });
    setEditingMeta(false);
  }

  async function handleDeleteSection(sectionId: string) {
    if (!id) return;
    await deleteSection(id, sectionId);
    setConfirmDeleteSection(null);
  }

  function handleStartRenameSection(sectionId: string, currentName: string) {
    setEditingSectionId(sectionId);
    setEditSectionName(currentName);
  }

  async function handleSaveRenameSection() {
    if (!id || !editingSectionId || !editSectionName.trim()) return;
    await renameSection(id, editingSectionId, editSectionName.trim());
    setEditingSectionId(null);
  }

  async function handleLoadFrames(sectionId: string) {
    if (pickingPosterSection === sectionId) {
      setPickingPosterSection(null);
      setSectionFrames([]);
      return;
    }
    setPickingPosterSection(sectionId);
    setLoadingFrames(true);
    setSectionFrames([]);
    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(apiUrl(`/api/shops/${id}/sections/${sectionId}/frames`), { headers });
      if (res.ok) {
        const data = await res.json();
        setSectionFrames(data.frames || []);
      }
    } catch {
      // ignore
    }
    setLoadingFrames(false);
  }

  async function handleSelectPosterFrame(sectionId: string, frameIndex: number) {
    if (!id) return;
    await updateSection(id, sectionId, { posterFrameIndex: frameIndex });
    // Clear the poster error cache and bust image cache so it reloads
    setSectionPosterErrors((prev) => {
      const next = new Set(prev);
      next.delete(sectionId);
      return next;
    });
    setPosterCacheBust((prev) => ({ ...prev, [sectionId]: Date.now() }));
    setPickingPosterSection(null);
    setSectionFrames([]);
  }

  async function handleSetShopPoster(sectionId: string) {
    if (!id) return;
    await updateShop(id, { posterSectionId: sectionId });
  }

  async function handleDelete() {
    if (!id) return;
    await deleteShop(id);
    navigate('/shops', { replace: true });
  }

  async function handleTransfer() {
    if (!id) return;
    setTransferring(true);
    const result = await initiateTransfer(id);
    setTransferring(false);
    setConfirmTransfer(false);
    if (!result) return;

    const claimUrl = `${window.location.origin}/claim/${result.token}`;

    // Try Web Share API first (mobile), fall back to clipboard
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('claimShopTitle', { name: shop?.name }),
          text: t('claimShopText', { name: shop?.name }),
          url: claimUrl,
        });
      } catch {
        // User cancelled share — link is still generated, show in UI
      }
    } else {
      await navigator.clipboard.writeText(claimUrl);
      alert(t('transferLinkCopied'));
    }
  }

  async function handleCancelTransfer() {
    if (!id) return;
    await cancelTransfer(id);
  }

  return (
    <div className="shop-detail-page">
      {/* Header */}
      <header className="shop-detail-header">
        <button className="shop-detail-back" onClick={() => navigate('/shops')}>{t('common:back')}</button>
        <div className="shop-detail-title-row">
          {editingName ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                className="sd-input sd-input-sm"
                style={{ flex: 1, fontSize: '16px', fontWeight: 600 }}
                autoFocus
              />
              <button className="sd-btn sd-btn-primary" onClick={handleSaveName}>{t('common:save')}</button>
              <button className="sd-btn" onClick={() => setEditingName(false)}>{t('common:cancel')}</button>
            </div>
          ) : (
            <>
              <h1 onClick={handleStartEditName} style={{ cursor: 'pointer' }} title={t('clickToEdit')}>
                {shop.name} <span style={{ fontSize: '14px', color: '#64748b' }}>✏️</span>
              </h1>
              {isPublic && <span className="visibility-badge visibility-public">{t('statusPublic')}</span>}
            </>
          )}
        </div>
        {shop.address && <p className="shop-detail-address">{shop.address}</p>}
      </header>

      {/* View in 3D */}
      <a
        className="shop-detail-btn-3d"
        href={`/?shopid=${shop.id}`}
        target="_blank"
        rel="noopener"
      >
        {t('viewShopIn3D')}
      </a>

      {/* Orders Dashboard */}
      <button
        className="shop-detail-btn-3d"
        style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', borderColor: '#f59e0b', marginTop: 0 }}
        onClick={() => navigate(`/dashboard/${shop.id}`)}
      >
        {t('ordersDashboard')}
      </button>

      {/* View All Frames */}
      {shop.sections.length > 0 && (
        <button
          className="shop-detail-btn-3d"
          style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', borderColor: '#10b981', marginTop: 0 }}
          onClick={() => {
            const sec = shop.sections[0];
            setGallerySection({ sectionId: sec.sectionId, name: sec.name });
          }}
        >
          📸 View All Frames
        </button>
      )}

      {/* Details & Metadata */}
      <section className="shop-detail-section">
        <h2 className="shop-detail-section-title">{t('details')}</h2>
        {editingMeta ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <label className="shop-detail-meta-label">{t('description')}</label>
              <textarea
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                className="sd-input sd-input-sm"
                style={{ width: '100%', minHeight: '60px', resize: 'vertical' }}
                placeholder={t('describeYourShop')}
              />
            </div>
            <div>
              <label className="shop-detail-meta-label">{t('address')}</label>
              <input
                type="text"
                value={metaAddress}
                onChange={(e) => setMetaAddress(e.target.value)}
                className="sd-input sd-input-sm"
                placeholder={t('shopAddress')}
              />
            </div>
            <div>
              <label className="shop-detail-meta-label">{t('phone')}</label>
              <input
                type="tel"
                value={metaPhone}
                onChange={(e) => setMetaPhone(e.target.value)}
                className="sd-input sd-input-sm"
                placeholder={t('phonePlaceholder')}
              />
            </div>
            <div>
              <label className="shop-detail-meta-label">{t('whatsApp')}</label>
              <input
                type="tel"
                value={metaWhatsapp}
                onChange={(e) => setMetaWhatsapp(e.target.value)}
                className="sd-input sd-input-sm"
                placeholder={t('phonePlaceholder')}
              />
            </div>
            <div>
              <label className="shop-detail-meta-label">{t('openingHours')}</label>
              <input
                type="text"
                value={metaOpeningHours}
                onChange={(e) => setMetaOpeningHours(e.target.value)}
                className="sd-input sd-input-sm"
                placeholder={t('openingHoursPlaceholder')}
              />
            </div>
            {/* Spacer so content isn't hidden under floating bar */}
            <div style={{ height: 72 }} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {shop.description ? (
              <p style={{ color: '#cbd5e1', fontSize: '13px', margin: 0 }}>{shop.description}</p>
            ) : (
              <p style={{ color: '#64748b', fontSize: '13px', margin: 0, fontStyle: 'italic' }}>{t('noDescription')}</p>
            )}
            <div className="shop-detail-meta-grid">
              <span className="shop-detail-meta-item">
                📍 {shop.address || <em style={{ color: '#64748b' }}>{t('noAddress')}</em>}
              </span>
              <span className="shop-detail-meta-item">
                📞 {shop.phone || <em style={{ color: '#64748b' }}>{t('noPhone')}</em>}
              </span>
              <span className="shop-detail-meta-item">
                💬 {shop.whatsapp || <em style={{ color: '#64748b' }}>{t('noWhatsApp')}</em>}
              </span>
              <span className="shop-detail-meta-item">
                🕐 {shop.openingHours || <em style={{ color: '#64748b' }}>{t('noHoursSet')}</em>}
              </span>
            </div>
            <button className="sd-btn" onClick={handleStartEditMeta} style={{ alignSelf: 'flex-start', marginTop: '4px' }}>
              {t('editDetails')}
            </button>
          </div>
        )}
      </section>

      {/* Location Section */}
      <section className="shop-detail-section">
        <h2 className="shop-detail-section-title">{t('location')}</h2>
        {shop.location ? (
          <div className="shop-detail-location-display">
            <a
              href={`https://maps.google.com/?q=${getLocLat(shop.location)},${getLocLng(shop.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shop-detail-location-map-thumb"
            >
              <img
                src={`https://maps.googleapis.com/maps/api/staticmap?center=${getLocLat(shop.location)},${getLocLng(shop.location)}&zoom=15&size=400x120&markers=color:red%7C${getLocLat(shop.location)},${getLocLng(shop.location)}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}`}
                alt="Map location"
                className="shop-detail-location-map-img"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
              <span className="shop-detail-location-map-label">📍 {t('locationSet', 'Location set')} — {t('viewOnMap')}</span>
            </a>
            <button className="sd-btn-tiny" onClick={handlePickLocationOnMap}>{t('common:edit')}</button>
          </div>
        ) : (
          <div className="shop-detail-location-display">
            <span className="shop-detail-location-missing">{t('noLocationSet')}</span>
            <button className="sd-btn" onClick={handlePickLocationOnMap}>{t('addLocation')}</button>
          </div>
        )}
      </section>

      {/* Visibility Section */}
      <section className="shop-detail-section">
        <h2 className="shop-detail-section-title">{t('visibility')}</h2>
        <div className="shop-detail-visibility-row">
          <span className="shop-detail-visibility-label">
            {isPublic ? t('publicVisibilityLabel') : t('privateVisibilityLabel')}
          </span>
          <button
            className={`sd-btn ${isPublic ? 'sd-btn-active' : ''}`}
            onClick={handleToggleVisibility}
          >
            {isPublic ? t('makePrivate') : t('makePublic')}
          </button>
        </div>
        {isPublic && (
          <div className="shop-detail-share-row">
            <span className="shop-detail-share-label">{t('shareLink')}:</span>
            <a
              className="shop-detail-share-link"
              href={`${window.location.origin}/shop/${shop.id}`}
              target="_blank"
              rel="noopener"
            >
              {window.location.origin}/shop/{shop.id}
            </a>
            <button
              className="shop-detail-share-copy"
              onClick={() => {
                const url = `${window.location.origin}/shop/${shop.id}`;
                navigator.clipboard.writeText(url).then(() => {
                  const btn = document.activeElement as HTMLButtonElement;
                  const orig = btn.textContent;
                  btn.textContent = t('copied');
                  setTimeout(() => { btn.textContent = orig; }, 1500);
                });
              }}
            >
              {t('copy')}
            </button>
          </div>
        )}
      </section>

      {/* Sections */}
      <section className="shop-detail-section">
        <h2 className="shop-detail-section-title">
          {t('sections')}
          <span className="shop-detail-section-count">{shop.sections.length}</span>
        </h2>

        {shop.sections.length === 0 ? (
          <p className="shop-detail-sections-empty">{t('noSectionsYet')}</p>
        ) : (
          <div className="shop-detail-sections-list">
            {shop.sections.map((sec) => {
              const sectionScans = scansBySection[sec.sectionId] || [];
              const latestReady = sectionScans.find((s) => s.status === 'ready');
              const latestActive = sectionScans.find((s) =>
                ['uploading', 'queued', 'processing'].includes(s.status)
              );
              return (
                <div key={sec.sectionId} className="shop-detail-section-item-wrapper">
                <div className="shop-detail-section-item">
                  {latestReady ? (
                    <a
                      className="shop-detail-section-poster shop-detail-section-poster-link"
                      href={`/?shopid=${latestReady.id}`}
                      target="_blank"
                      rel="noopener"
                      title={t('viewSectionIn3D')}
                    >
                      {!sectionPosterErrors.has(sec.sectionId) ? (
                        <img
                          src={apiUrl(`/api/shops/${id}/poster?section_id=${sec.sectionId}${token ? `&token=${token}` : ''}${posterCacheBust[sec.sectionId] ? `&_t=${posterCacheBust[sec.sectionId]}` : ''}`)}
                          alt=""
                          loading="lazy"
                          className="shop-detail-section-poster-img"
                          onError={() => handleSectionPosterError(sec.sectionId)}
                        />
                      ) : (
                        <div className="shop-detail-section-poster-fallback">📷</div>
                      )}
                    </a>
                  ) : (
                    <div className="shop-detail-section-poster">
                      {!sectionPosterErrors.has(sec.sectionId) ? (
                        <img
                          src={apiUrl(`/api/shops/${id}/poster?section_id=${sec.sectionId}${token ? `&token=${token}` : ''}${posterCacheBust[sec.sectionId] ? `&_t=${posterCacheBust[sec.sectionId]}` : ''}`)}
                          alt=""
                          loading="lazy"
                          className="shop-detail-section-poster-img"
                          onError={() => handleSectionPosterError(sec.sectionId)}
                        />
                      ) : (
                        <div className="shop-detail-section-poster-fallback">📷</div>
                      )}
                    </div>
                  )}
                  <div className="shop-detail-section-info">
                    {editingSectionId === sec.sectionId ? (
                      <input
                        type="text"
                        value={editSectionName}
                        onChange={(e) => setEditSectionName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRenameSection();
                          if (e.key === 'Escape') setEditingSectionId(null);
                        }}
                        className="sd-input sd-input-sm"
                        style={{ fontSize: '13px', padding: '3px 6px', width: '120px' }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="shop-detail-section-name shop-detail-section-name-editable"
                        onClick={() => handleStartRenameSection(sec.sectionId, sec.name)}
                        title={t('clickToRename')}
                      >
                        {sec.name}
                      </span>
                    )}
                    {editingSectionId !== sec.sectionId && latestActive && <span className={`scan-badge badge-${latestActive.status}`}>{statusLabel(latestActive.status)}</span>}
                    {editingSectionId !== sec.sectionId && !latestActive && latestReady && <span className="scan-badge badge-ready">{t('statusReady')}</span>}
                  </div>
                  {/* Row 1 inline: View button */}
                  <div className="shop-detail-section-actions-inline">
                    {editingSectionId === sec.sectionId && (
                      <div className="shop-detail-rename-btns">
                        <button className="sd-btn-tiny" onClick={handleSaveRenameSection}>✓</button>
                        <button className="sd-btn-tiny" onClick={() => setEditingSectionId(null)}>✕</button>
                      </div>
                    )}
                    {editingSectionId !== sec.sectionId && latestReady && (
                      <a
                        className="sd-btn-view"
                        href={`/?shopid=${latestReady.id}`}
                        target="_blank"
                        rel="noopener"
                      >
                        👁 {t('view')}
                      </a>
                    )}
                  </div>
                </div>
                {/* Row 2: secondary actions */}
                <div className="shop-detail-section-actions-row2">
                  {!sectionPosterErrors.has(sec.sectionId) && (
                    <button
                      className={`sd-btn-tiny${pickingPosterSection === sec.sectionId ? ' sd-btn-active' : ''}`}
                      onClick={() => handleLoadFrames(sec.sectionId)}
                    >
                      {t('poster')}
                    </button>
                  )}
                  {!sectionPosterErrors.has(sec.sectionId) && shop.posterSectionId !== sec.sectionId && (
                    <button
                      className="sd-btn-tiny"
                      onClick={() => handleSetShopPoster(sec.sectionId)}
                      title={t('setAsShopPoster')}
                    >
                      {t('setCover')}
                    </button>
                  )}
                  {shop.posterSectionId === sec.sectionId && (
                    <span className="sd-shop-poster-badge" title={t('currentShopPoster')}>⭐ {t('cover')}</span>
                  )}
                  <button
                    className="sd-btn-tiny"
                    onClick={() => setGallerySection({ sectionId: sec.sectionId, name: sec.name })}
                    title="View all frames"
                  >
                    📸
                  </button>
                  <button
                    className="sd-btn-scan"
                    onClick={() => handleScan(sec.sectionId, sec.name)}
                  >
                    📷 {t('scan')}
                  </button>
                  <div style={{ marginLeft: 'auto' }}>
                    {confirmDeleteSection === sec.sectionId ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="sd-btn-delete-section sd-btn-delete-confirm"
                          onClick={() => handleDeleteSection(sec.sectionId)}
                        >
                          ✓
                        </button>
                        <button
                          className="sd-btn-delete-section"
                          onClick={() => setConfirmDeleteSection(null)}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        className="sd-btn-delete-section"
                        onClick={() => setConfirmDeleteSection(sec.sectionId)}
                        title={t('deleteSection')}
                      >
                        🗑
                      </button>
                    )}
                  </div>
                </div>
                {pickingPosterSection === sec.sectionId && (
                  <div className="shop-detail-frame-picker">
                    {loadingFrames ? (
                      <div className="shop-detail-frame-picker-loading">{t('loadingFrames')}</div>
                    ) : sectionFrames.length === 0 ? (
                      <div className="shop-detail-frame-picker-loading">{t('noFramesFound')}</div>
                    ) : (
                      sectionFrames.map((frame) => (
                        <button
                          key={frame.index}
                          className={`shop-detail-frame-thumb${sec.posterFrameIndex === frame.index ? ' selected' : ''}`}
                          onClick={() => handleSelectPosterFrame(sec.sectionId, frame.index)}
                          title={`Frame ${frame.index}`}
                        >
                          <img
                            src={apiUrl(frame.url + (token ? `?token=${token}` : ''))}
                            alt={`Frame ${frame.index}`}
                            loading="lazy"
                          />
                        </button>
                      ))
                    )}
                  </div>
                )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add Section */}
        <div className="shop-detail-add-section">
          <input
            type="text"
            placeholder={t('newSectionName')}
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            className="sd-input sd-input-sm"
            onKeyDown={(e) => e.key === 'Enter' && handleAddSection()}
          />
          <button className="sd-btn" onClick={handleAddSection}>{t('add')}</button>
        </div>
      </section>

      {/* Scan History */}
      <ScanHistorySection
        shopId={id!}
        scans={shopScans}
        onRefresh={() => fetchShopScans(id!)}
        onDelete={deleteScan}
        onRetry={retryScan}
      />

      {/* Transfer Ownership */}
      <section className="shop-detail-section">
        <h2 className="shop-detail-section-title">{t('transferOwnership')}</h2>
        {shop.pendingTransfer ? (
          <div className="shop-detail-transfer-pending">
            <p className="shop-detail-transfer-status">
              {t('transferLinkGenerated', { expires: new Date(shop.pendingTransfer.expiresAt).toLocaleString() })}
            </p>
            <div className="shop-detail-transfer-actions">
              <button
                className="sd-btn"
                onClick={() => {
                  const url = `${window.location.origin}/claim/${shop.pendingTransfer!.token}`;
                  if (navigator.share) {
                    navigator.share({ title: t('claimShopTitle', { name: shop.name }), url }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(url).then(() => alert(t('linkCopied')));
                  }
                }}
              >
                {t('shareLinkBtn')}
              </button>
              <button className="sd-btn-danger" onClick={handleCancelTransfer}>
                {t('cancelTransfer')}
              </button>
            </div>
          </div>
        ) : !confirmTransfer ? (
          <button className="sd-btn" onClick={() => setConfirmTransfer(true)}>
            🔄 {t('transferOwnership')}
          </button>
        ) : (
          <div className="shop-detail-confirm-delete">
            <p>{t('generateTransferLinkPrompt')}</p>
            <div className="shop-detail-confirm-actions">
              <button
                className="sd-btn sd-btn-primary"
                onClick={handleTransfer}
                disabled={transferring}
              >
                {transferring ? t('generating') : t('generateLink')}
              </button>
              <button className="sd-btn" onClick={() => setConfirmTransfer(false)}>{t('common:cancel')}</button>
            </div>
          </div>
        )}
      </section>

      {/* Danger Zone */}
      <section className="shop-detail-section shop-detail-danger-zone">
        <h2 className="shop-detail-section-title shop-detail-danger-title">{t('dangerZone')}</h2>
        {!confirmDelete ? (
          <button className="sd-btn-danger" onClick={() => setConfirmDelete(true)}>
            {t('deleteShop')}
          </button>
        ) : (
          <div className="shop-detail-confirm-delete">
            <p>{t('confirmDeleteShop', { name: shop.name })}</p>
            <div className="shop-detail-confirm-actions">
              <button className="sd-btn-danger sd-btn-danger-confirm" onClick={handleDelete}>
                {t('yesDelete')}
              </button>
              <button className="sd-btn" onClick={() => setConfirmDelete(false)}>{t('common:cancel')}</button>
            </div>
          </div>
        )}
      </section>

      {/* Floating Save/Publish bar — visible when editing meta */}
      {editingMeta && (
        <div className="sd-floating-bar">
          <button className="sd-floating-btn sd-floating-btn-cancel" onClick={() => setEditingMeta(false)}>
            {t('common:cancel')}
          </button>
          <button className="sd-floating-btn sd-floating-btn-save" onClick={handleSaveMeta}>
            💾 {t('common:save')}
          </button>
          <button
            className="sd-floating-btn sd-floating-btn-publish"
            onClick={async () => { await handleSaveMeta(); await handleToggleVisibility() }}
            disabled={isPublic}
          >
            🌐 {t('makePublic')}
          </button>
        </div>
      )}

      {/* Frame Gallery Overlay */}
      {gallerySection && (
        <FrameGalleryOverlay
          shopId={id!}
          sectionId={gallerySection.sectionId}
          sectionName={gallerySection.name}
          onClose={() => setGallerySection(null)}
        />
      )}
    </div>
  );
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    uploading: '⬆ Uploading',
    queued: '⏳ Queued',
    processing: '⚙ Processing',
    ready: '✓ Ready',
    failed: '✗ Failed',
  };
  return map[status] || status;
}
